/**
 * Creating a missing record from wherever one is being picked (Dynamics' quick create):
 * what the person typed becomes the new record's name, what the form around already knows
 * (its account, say) is carried over, and the saved record goes back into the field.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE = /^\+?[\d\s()-]{6,}$/

/** The typed text as the new record's own fields. */
export function prefillFromText(entity, text) {
  const q = String(text || '').trim()
  if (!q) return {}
  if (entity === 'contact') {
    if (EMAIL.test(q)) return { contact_points: [{ channel: 'email', kind: 'work', value: q, is_primary: true, label: null }] }
    if (PHONE.test(q)) return { contact_points: [{ channel: 'phone', kind: 'work', value: q, is_primary: true, label: null }] }
    const [first, ...rest] = q.split(/\s+/)
    return rest.length ? { first_name: first, last_name: rest.join(' ') } : { last_name: first }
  }
  if (entity === 'lead') {
    if (EMAIL.test(q)) return { email: q }
    return { topic: q }
  }
  if (entity === 'account' && EMAIL.test(q)) {
    return { name: q.split('@')[1].split('.')[0], contact_points: [{ channel: 'email', kind: 'main', value: q, is_primary: true, label: null }] }
  }
  const field = { account: 'name', opportunity: 'name', activity: 'subject', offer: 'title', competitor: 'name' }[entity] || 'name'
  return { [field]: q }
}

/**
 * What the form a record is created from already says about it: every reference the new
 * record has in common with that form (a contact created from an opportunity gets the
 * opportunity's account). `inputs` and `refs` are the parent form's.
 */
export function inheritFromForm(meta, targetEntity, sourceEntity, inputs = {}, refs = {}) {
  const out = {}
  const labels = {}
  const sourceRefs = new Map(meta.fields(sourceEntity).filter((f) => f.type === 'ref').map((f) => [f.input, f]))
  for (const field of meta.fields(targetEntity)) {
    if (field.type !== 'ref' || field.readonly) continue
    const value = inputs[field.input]
    if (!value) continue
    const source = sourceRefs.get(field.input)
    if (!source) continue
    out[field.input] = value
    if (refs[source.name]) labels[field.name] = refs[source.name]
  }
  // A record being filed on something (an activity, a note) keeps what it is about.
  if (Object.keys(labels).length) out._refs = labels
  return out
}

/** A label for a record that may exist only in the queue so far. */
export function labelOf(entity, record = {}) {
  if (record.label) return record.label
  if (entity === 'contact') return [record.first_name, record.last_name].filter(Boolean).join(' ') || record.contact_points?.[0]?.value || ''
  return record.name || record.topic || record.subject || record.title || ''
}
